import React, { useCallback } from 'react';
import { Col, Row } from 'antd';
import DOMPurify from 'isomorphic-dompurify';
import { get } from 'lodash';
import styled from '@emotion/styled';

import FormValueWithDataSource from './form-value-with-data-source';

import {
  FormComponentType,
  IElementSchema,
  IFormData,
  IHiddenFields,
  IOption,
  IPageSchema,
} from '../../../../types/form';
import { isPanel as getIsPanel, getPath } from '../../../../utils/form';
import { IDocument } from '../../../../types/models/document';
import { OptionalLabel } from '../element-list';
import { formatDate } from '../../../../utils/date';
import { hasRequiredValidation } from '../../../../utils/form-builder';
import theme from '../../../../theme';

export type PageReviewProps = {
  sectionIndex: number;
  pageIndex: number;
  page: IPageSchema;
  formData: IFormData;
  hiddenFields: IHiddenFields;
  validated: boolean;
  isAdminView?: boolean;
  onEditClick: (sectionIndex: number, pageIndex: number) => void;
};

const Container = styled(Row)`
  background-color: #fff;
  margin-bottom: 20px;
`;

const Header = styled(Row)`
  padding: 20px 30px;
  border-bottom: 1px solid #dedede;
`;

const Title = styled(Col)`
  font-size: 18px;
  font-weight: bold;
  color: #000;
`;

const Edit = styled(Col)`
  display: flex;
  justify-content: flex-end;
`;

const EditLink = styled(Col)`
  font-size: 16px;
  font-weight: bold;
  text-decoration: underline;
  color: ${theme.colorsPrimaryBlue};
  cursor: pointer;
`;

const Body = styled(Row)`
  padding: 0 30px 30px 30px;
`;

const FormTitle = styled(Col)`
  font-size: 16px;
  font-weight: bold;
  color: #000;
  margin-top: 30px;
`;

const ListItem = styled.li<{ isHidden: boolean }>`
  display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
`;

export const FormValue = styled(Col)`
  font-size: 16px;
  color: #000;
  margin-top: 10px;
  min-height: 16px; /* in case the value is empty */
  white-space: pre-wrap; /* support line break */
`;

/**
 * Ensure the list formatting follows the RTE component display
 */
const RTEFormValue = styled(FormValue)`
  > ol,
  ul {
    list-style: initial;
    margin-left: 3em;
  }
  > ol {
    list-style-type: decimal;
  }
`;

const ErrorMessage = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  font-size: 16px;
  color: #f00;
  border-top: 1px solid #dedede;
`;

const OrderedNumberCol = styled(Col)`
  padding-top: 30px;
  min-width: 40px; // to support at least two digits i.e. 10.
  text-align: right;
`;

const OrderedContentCol = styled(Col)`
  flex: 1;
`;

export function renderElements({
  elements: parentElements,
  hiddenFields,
  formData,
  path,
  index = -1,
  parentType,
  parentOptions,
}: {
  elements: IElementSchema[];
  hiddenFields: IHiddenFields;
  formData?: IFormData;
  path?: string;
  index?: number;
  parentType?: FormComponentType;
  parentOptions?: IOption[];
}) {
  const nameList = new Set();
  const formItems = parentElements
    // hide label component or the hidden field
    .filter(
      ({ type }) =>
        ![FormComponentType.Label, FormComponentType.LinkToPage, FormComponentType.DocumentTypeMetadataTitle].includes(
          type
        )
    )
    .map(element => {
      const { type, title, name, id, elements, validation, isOrderedList, dataSource, showTime, options } = element;
      const isRequired = hasRequiredValidation(validation);
      const isPanel = getIsPanel(type);
      const elementPath = getPath(name, path && index >= 0 ? `${path}[${index}]` : path);
      let value = get(formData, name);
      const origValue = value;

      // do not count the same field name twice within the same elements such as `address_state` in address component
      if (nameList.has(name)) {
        return null;
      }
      nameList.add(name);

      // do not render hidden fields
      if (hiddenFields[elementPath]) {
        return null;
      }

      if (elements) {
        if (Array.isArray(value)) {
          // repeatable container case
          value = value.map((item, index) => {
            const children = renderElements({
              elements,
              hiddenFields,
              formData: item,
              path: elementPath,
              index,
              parentType: type,
            });

            if (!isOrderedList) {
              return children;
            }

            return (
              <Row key={index}>
                <OrderedNumberCol>
                  <p>
                    <strong>{index + 1}.</strong>
                  </p>
                </OrderedNumberCol>
                <OrderedContentCol>{children}</OrderedContentCol>
              </Row>
            );
          });
        } else if (typeof value === 'object') {
          // non-repeatable container case
          value = renderElements({
            elements,
            hiddenFields,
            formData: value,
            path: elementPath,
            parentType: type,
            parentOptions: options,
          });
        } else if (!value) {
          value = null;
        } else {
          // unexpected case
          console.error('Unexpected form data format found', name, value);
          value = JSON.stringify(value);
        }
      } else {
        if (type === FormComponentType.Date && value) {
          let timezoneName;
          // if date picker is part of `datetime` panel, grab timezone value to display the date value
          // with user selected timezone abbr
          if (parentType === FormComponentType.DateTime) {
            const { name: timezoneFieldName = '' } =
              parentElements.find(({ type }) => type === FormComponentType.Dropdown) || {};
            timezoneName = get(formData, timezoneFieldName);
          }
          const dateFormat = !showTime ? 'MMM DD, YYYY' : undefined;
          // date value can be a string when populated straight from the server
          // or Dayjs object if updated by Date/Time Picker
          value = formatDate(value.toISOString ? value.toISOString() : value, dateFormat, timezoneName);
        } else if (type === FormComponentType.FileUpload && Array.isArray(value)) {
          value = value.map(({ fileName }: IDocument) => fileName).join('\n');
        } else if (type === FormComponentType.Checkbox) {
          value = value ? 'Checked' : 'Unchecked';
        }
        // e.g. checkboxgroup values
        if (Array.isArray(value)) {
          if (options) {
            value = options.filter(option => value.includes(option.value)).map(option => option.label);
          }
          value = value.join('\n');
        }

        if (value && type === FormComponentType.Radiogroup && options) {
          value = options.find(option => option.value === value)?.label;
        }

        if (
          parentOptions &&
          parentType === FormComponentType.DragNDropOptionPanel &&
          type === FormComponentType.DragNDropOption
        ) {
          value = parentOptions.find(option => option.value === value)?.label;
        }
      }

      return (
        <ListItem isHidden={type === 'hidden'} key={id}>
          <FormTitle>
            {title}
            {!isRequired && !isPanel && (
              <>
                &nbsp;<OptionalLabel>(Optional)</OptionalLabel>
              </>
            )}
          </FormTitle>
          {type === FormComponentType.RichTextEditor ? (
            <RTEFormValue dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
          ) : dataSource ? (
            <FormValueWithDataSource dataSource={dataSource} type={type}>
              {origValue}
            </FormValueWithDataSource>
          ) : (
            <FormValue>{value}</FormValue>
          )}
        </ListItem>
      );
    });

  return (
    <Body key={index}>
      <ul>{formItems}</ul>
    </Body>
  );
}

const PageReview = ({
  sectionIndex,
  pageIndex,
  page,
  formData,
  hiddenFields,
  validated,
  onEditClick,
  isAdminView = false,
}: PageReviewProps) => {
  const handleEditClick = useCallback(() => {
    onEditClick(sectionIndex, pageIndex);
  }, [onEditClick, pageIndex, sectionIndex]);

  return (
    <Container>
      <Header align="middle">
        <Title span={18}>{page.title}</Title>
        {!isAdminView && (
          <Edit span={6}>
            <EditLink onClick={handleEditClick}>Edit</EditLink>
          </Edit>
        )}
      </Header>
      {renderElements({ elements: page.elements, hiddenFields, formData })}
      {!validated && !isAdminView && <ErrorMessage> You have not completed this page.</ErrorMessage>}
    </Container>
  );
};

export default PageReview;
